import React from "react";

// ReturnType defines what will be returned from this hook
type ReturnType<Params> = [
  currentParamState: Params,
  setParamsCallback: (newParams: Params) => void,
  handleParamReset: () => void,
  refetchDefault: boolean,
  setRefetchDefault: React.Dispatch<React.SetStateAction<boolean>>,
];

function removeUndefinedValues<T extends object>(obj: T): T {
  return Object.entries(obj).reduce((acc: any, [key, value]) => {
    if (value !== undefined) {
      acc[key] = value;
    }
    return acc;
  }, {}) as T;
}

const useParamChange = <Params extends Partial<Record<string, any>>>(
  defaultParams: Params,
): ReturnType<Params> => {
  // Current param state, will be used to pass params to rtk query hook
  const [currentParamState, setCurrentParamState] = React.useState<Params>(defaultParams);

  const [refetchDefault, setRefetchDefault] = React.useState<boolean>(false);

  // Change params callback, this will be used to trigger the changing of params
  const setParamsCallback = React.useCallback(
    (newParams: Params): void => {
      setCurrentParamState((prev: Params): Params => {
        return removeUndefinedValues({
          ...prev,
          ...newParams,
        });
      });
    },
    [setCurrentParamState],
  );

  // Reset params callback
  const handleParamReset = React.useCallback((): void => {
    setCurrentParamState(defaultParams);
    setRefetchDefault(true);
  }, [setCurrentParamState, defaultParams]);

  return [
    currentParamState,
    setParamsCallback,
    handleParamReset,
    refetchDefault,
    setRefetchDefault,
  ];
};
export default useParamChange;
